/**
 * Weapon definitions — the full Gun Game roster. All combat outcomes are
 * resolved server-side from these configs; the client reads them for feel
 * (recoil, ADS, viewmodel) only.
 */
export const MELEE = {
    range: 2.4,
    /** cos of half the melee cone (0.5 → 60° half-angle). */
    facingCos: 0.5,
    cooldownMs: 800,
};
export const COMBAT = {
    headshotTopFraction: 0.25,
    respawnDelayMs: 1500,
    maxHp: 100,
    /** Headshots are always an instant kill. */
    headshotInstakill: true,
    /**
     * A single BODY hit can't reduce a full-health player below this HP — so no
     * body shot ever one-shots you; it always takes a follow-up. Headshots and
     * melee ignore this.
     */
    minSurviveHp: 15,
    /** Server rewind = client interp buffer + this slack (half-RTT allowance). */
    lagCompExtraMs: 50,
};
const W = (cfg) => cfg;
/** IMPERION beam rifle (chest weapon, outside the ladder) — balance hooks. */
export const IMPERION = {
    hipDamage: 26,
    hipRate: 240, // ~4/s
    hipBloom: 0.013,
    moveBloomMul: 2.2, // spread grows while moving
    chargeMs: 1200,
    shotsPerChest: 3, // charges per pickup (also drives the per-shot sound pitch)
    minChargeFrac: 0.5, // must charge to at least 50% before it will fire at all
    minChargeDamage: 40,
    maxChargeDamage: 150, // partial-charge ceiling — FULL charge is an instant kill
    cooldownMs: 400, // between shots from the same energy cell
    falloff: [60, 130, 0.75],
    beamWidth: 1.15, // hitbox inflation — the fat neon bolt scorches a wide lane
    zoomFov: 30, // x3 zoom (base FOV 90)
    reticleColor: "#00f0ff",
};
export const WEAPONS = {
    // IMPERION — energy precision rifle from a chest (never in the ladder).
    // Hipfire config lives here; the ADS charge mechanics come from IMPERION.
    imperion: W({
        id: "imperion",
        displayName: "IMPERION",
        damage: IMPERION.hipDamage,
        headshotMultiplier: 2,
        falloff: IMPERION.falloff,
        fireRate: IMPERION.hipRate,
        magSize: 999, // energy cell — no reload cycle
        reloadTimeMs: 0,
        recoilPattern: [{ pitch: 0.005, yaw: 0.001 }],
        bloom: IMPERION.hipBloom,
        adsTimeMs: 140,
        adsSpreadMultiplier: 0,
        hitscan: true,
        auto: true,
    }),
    knife: W({
        id: "knife",
        displayName: "NEON FANG",
        damage: 100,
        headshotMultiplier: 1,
        falloff: [MELEE.range, MELEE.range, 1],
        fireRate: 75,
        magSize: 0,
        reloadTimeMs: 0,
        recoilPattern: [],
        bloom: 0,
        adsTimeMs: 0,
        adsSpreadMultiplier: 1,
        hitscan: false,
        melee: true,
    }),
    // Final tier: a thrown, spinning ballistic knife. Firing launches a knife
    // projectile that flies and kills on contact (a hit wins the match). You
    // carry two; "reload" redraws a fresh blade.
    ballistic: W({
        id: "ballistic",
        displayName: "BALLISTIC FANG",
        damage: 100,
        headshotMultiplier: 1,
        falloff: [200, 200, 1],
        fireRate: 90,
        magSize: 2,
        reloadTimeMs: 850,
        recoilPattern: [],
        bloom: 0,
        adsTimeMs: 120,
        adsSpreadMultiplier: 1,
        hitscan: false,
        thrown: true,
    }),
    pistol: W({
        id: "pistol",
        displayName: "P-9 VECTOR",
        damage: 26,
        headshotMultiplier: 2,
        falloff: [12, 35, 0.65],
        fireRate: 380,
        magSize: 12,
        reloadTimeMs: 1300,
        recoilPattern: [
            { pitch: 0.014, yaw: 0.002 },
            { pitch: 0.016, yaw: -0.003 },
            { pitch: 0.018, yaw: 0.004 },
        ],
        bloom: 0.012,
        adsTimeMs: 180,
        adsSpreadMultiplier: 0.35,
        hitscan: true,
    }),
    machine_pistol: W({
        id: "machine_pistol",
        displayName: "HORNET MK.II",
        damage: 16,
        headshotMultiplier: 1.8,
        falloff: [10, 28, 0.6],
        fireRate: 850,
        magSize: 28,
        reloadTimeMs: 1200,
        recoilPattern: [
            { pitch: 0.011, yaw: 0.004 },
            { pitch: 0.013, yaw: -0.005 },
            { pitch: 0.015, yaw: 0.006 },
            { pitch: 0.016, yaw: -0.004 },
        ],
        bloom: 0.02,
        adsTimeMs: 160,
        adsSpreadMultiplier: 0.45,
        hitscan: true,
        auto: true,
    }),
    smg: W({
        id: "smg",
        displayName: "RAZOR SMG",
        damage: 21,
        headshotMultiplier: 1.8,
        falloff: [14, 32, 0.6],
        fireRate: 720,
        magSize: 30,
        reloadTimeMs: 1500,
        recoilPattern: [
            { pitch: 0.01, yaw: 0.003 },
            { pitch: 0.012, yaw: -0.004 },
            { pitch: 0.013, yaw: 0.005 },
            { pitch: 0.014, yaw: -0.005 },
            { pitch: 0.015, yaw: 0.004 },
        ],
        bloom: 0.016,
        adsTimeMs: 190,
        adsSpreadMultiplier: 0.4,
        hitscan: true,
        auto: true,
    }),
    shotgun: W({
        id: "shotgun",
        displayName: "STREET SWEEPER",
        damage: 13, // per pellet x 12 pellets — double-barrel: 2 brutal shells, snappy reload
        headshotMultiplier: 1.35,
        falloff: [6, 16, 0.25],
        fireRate: 110,
        magSize: 2,
        reloadTimeMs: 1250,
        recoilPattern: [{ pitch: 0.06, yaw: 0.012 }],
        bloom: 0.075,
        adsTimeMs: 260,
        adsSpreadMultiplier: 0.8,
        hitscan: true,
        pellets: 12,
    }),
    pump_shotgun: W({
        id: "pump_shotgun",
        displayName: "JUNKYARD KING",
        damage: 15, // per pellet × 8 pellets
        headshotMultiplier: 1.4,
        falloff: [6, 16, 0.2],
        fireRate: 80,
        magSize: 6,
        reloadTimeMs: 2200,
        recoilPattern: [{ pitch: 0.05, yaw: 0.006 }],
        bloom: 0.05,
        adsTimeMs: 240,
        adsSpreadMultiplier: 0.75,
        hitscan: true,
        pellets: 8,
    }),
    carbine: W({
        id: "carbine",
        displayName: "CK-77 CARBINE",
        damage: 29,
        headshotMultiplier: 1.9,
        falloff: [20, 45, 0.7],
        fireRate: 560,
        magSize: 24,
        reloadTimeMs: 1600,
        recoilPattern: [
            { pitch: 0.012, yaw: 0.002 },
            { pitch: 0.014, yaw: -0.003 },
            { pitch: 0.015, yaw: 0.004 },
            { pitch: 0.016, yaw: -0.003 },
        ],
        bloom: 0.012,
        adsTimeMs: 220,
        adsSpreadMultiplier: 0.3,
        hitscan: true,
        auto: true,
    }),
    rifle: W({
        id: "rifle",
        displayName: "AURORA AR",
        damage: 30,
        headshotMultiplier: 2,
        falloff: [24, 55, 0.7],
        fireRate: 620,
        magSize: 30,
        reloadTimeMs: 1900,
        recoilPattern: [
            { pitch: 0.013, yaw: 0.003 },
            { pitch: 0.015, yaw: -0.004 },
            { pitch: 0.017, yaw: 0.005 },
            { pitch: 0.018, yaw: -0.005 },
            { pitch: 0.019, yaw: 0.006 },
            { pitch: 0.02, yaw: -0.004 },
        ],
        bloom: 0.013,
        adsTimeMs: 240,
        adsSpreadMultiplier: 0.28,
        hitscan: true,
        auto: true,
    }),
    burst_rifle: W({
        id: "burst_rifle",
        displayName: "TRIAD BURST",
        damage: 36, // one full 3-round burst on target = 108 → next tap kills
        headshotMultiplier: 2,
        falloff: [26, 60, 0.7],
        fireRate: 900, // within burst
        magSize: 24,
        reloadTimeMs: 1800,
        recoilPattern: [
            { pitch: 0.015, yaw: 0.002 },
            { pitch: 0.017, yaw: 0.003 },
            { pitch: 0.02, yaw: 0.004 },
        ],
        bloom: 0.01,
        adsTimeMs: 230,
        adsSpreadMultiplier: 0.25,
        hitscan: true,
        burst: 3,
        burstCooldownMs: 260,
    }),
    lmg: W({
        id: "lmg",
        displayName: "BULWARK LMG",
        damage: 32,
        headshotMultiplier: 1.7,
        falloff: [22, 60, 0.75],
        fireRate: 540,
        magSize: 60,
        reloadTimeMs: 3000,
        recoilPattern: [
            { pitch: 0.016, yaw: 0.006 },
            { pitch: 0.018, yaw: -0.007 },
            { pitch: 0.02, yaw: 0.008 },
            { pitch: 0.021, yaw: -0.008 },
        ],
        bloom: 0.022,
        adsTimeMs: 320,
        adsSpreadMultiplier: 0.4,
        hitscan: true,
        auto: true,
    }),
    dmr: W({
        id: "dmr",
        displayName: "LONGLINE DMR",
        damage: 58,
        headshotMultiplier: 2,
        falloff: [35, 70, 0.85],
        fireRate: 240,
        magSize: 10,
        reloadTimeMs: 2000,
        recoilPattern: [{ pitch: 0.03, yaw: 0.004 }, { pitch: 0.034, yaw: -0.005 }],
        bloom: 0.008,
        adsTimeMs: 280,
        adsSpreadMultiplier: 0.12,
        hitscan: true,
    }),
    sniper: W({
        id: "sniper",
        displayName: "GHOSTLINE",
        damage: 120,
        headshotMultiplier: 1.5,
        falloff: [50, 120, 1], // no falloff — full damage at any range
        fireRate: 50,
        magSize: 5,
        reloadTimeMs: 2600,
        recoilPattern: [{ pitch: 0.07, yaw: 0.008 }],
        bloom: 0.09,
        adsTimeMs: 380,
        adsSpreadMultiplier: 0.02,
        hitscan: true,
        oneShot: true, // bolt-action: a clean body shot one-shots a full-HP target
    }),
    magnum: W({
        id: "magnum",
        displayName: "DUSTMAKER .44",
        damage: 65, // hand cannon: a confident 2-tap at close-mid range
        headshotMultiplier: 2,
        falloff: [15, 40, 0.7],
        fireRate: 180,
        magSize: 6,
        reloadTimeMs: 2000,
        recoilPattern: [{ pitch: 0.05, yaw: 0.01 }],
        bloom: 0.014,
        adsTimeMs: 220,
        adsSpreadMultiplier: 0.25,
        hitscan: true,
    }),
};
export function weaponOrDefault(id) {
    return (id && WEAPONS[id]) || WEAPONS.pistol;
}
export function weaponType(cfg) {
    if (cfg.melee)
        return "Melee";
    if (cfg.thrown)
        return "Thrown";
    if (cfg.pellets)
        return "Shotgun";
    if (cfg.burst)
        return "Burst rifle";
    if (cfg.oneShot || cfg.id === "sniper")
        return "Sniper rifle";
    if (cfg.id === "dmr")
        return "Marksman rifle";
    if (cfg.id === "lmg")
        return "LMG";
    if (cfg.auto && cfg.fireRate >= 700)
        return "SMG";
    if (cfg.auto)
        return "Assault rifle";
    if (cfg.id.includes("pistol") || cfg.id === "magnum")
        return "Sidearm";
    return "Rifle";
}
export function weaponRarity(cfg) {
    const shotDamage = cfg.damage * (cfg.pellets ?? 1);
    if (cfg.id === "imperion" || cfg.oneShot || cfg.thrown)
        return "Legendary";
    // The magnum is a mid-tier sidearm by scarcity even though its shot damage
    // (65) would otherwise trip the Rare threshold below — pin it before that
    // check so the intended label actually shows (the old `|| cfg.id === "magnum"`
    // on the Uncommon line was dead code, the Rare check always won first).
    if (cfg.id === "magnum")
        return "Uncommon";
    if (shotDamage >= 100 || cfg.id === "lmg")
        return "Epic";
    if (shotDamage >= 55 || cfg.burst)
        return "Rare";
    if (cfg.auto)
        return "Uncommon";
    return "Common";
}
export function weaponTraits(cfg) {
    const traits = [];
    if (cfg.melee)
        traits.push("silent melee");
    if (cfg.thrown)
        traits.push("arcing projectile");
    if (cfg.oneShot)
        traits.push("body-shot lethal");
    if (cfg.pellets)
        traits.push(`${cfg.pellets} pellets`);
    if (cfg.burst)
        traits.push(`${cfg.burst}-round burst`);
    if (cfg.auto)
        traits.push("automatic");
    if (cfg.reloadTimeMs === 0)
        traits.push("no reload");
    else if (cfg.reloadTimeMs >= 3000)
        traits.push("slow reload");
    else if (cfg.reloadTimeMs <= 1500)
        traits.push("fast reload");
    if (cfg.magSize > 0 && cfg.magSize <= 6)
        traits.push("low capacity");
    else if (cfg.magSize >= 50)
        traits.push("large magazine");
    if (cfg.bloom <= 0.01)
        traits.push("accurate");
    else if (cfg.bloom >= 0.05)
        traits.push("wide spread");
    return traits.slice(0, 4);
}
export function weaponSummary(id) {
    const cfg = weaponOrDefault(id);
    const ammo = cfg.melee ? "no ammo" : `${cfg.magSize} mag`;
    const dmg = cfg.pellets ? `${cfg.damage} x ${cfg.pellets}` : String(cfg.damage);
    return `${cfg.displayName} - ${weaponType(cfg)} - ${weaponRarity(cfg)} - ${dmg} damage - ${ammo}`;
}
/** LONG SHOT modifier — the inverted falloff curve. Applied INSTEAD of the
 *  weapon's own falloff so every gun obeys the same "far = strong" law (uniform
 *  and legible): point-blank barely scratches, cross-map hits hardest. A shared
 *  multiplier on `cfg.damage`, so per-weapon power ranking is preserved while the
 *  distance relationship inverts. Neutral (1.0×) at LONGSHOT_NEUTRAL_DIST.
 *  Near/far are clamped so nothing becomes a zero-damage tickle or an
 *  unbounded cannon. */
export const LONGSHOT_NEAR_MUL = 0.5; // ≤6 m — brawling is weak
export const LONGSHOT_NEUTRAL_DIST = 18; // the crossover to full damage
export const LONGSHOT_FAR_DIST = 45; // ≥ here the gain caps out
export const LONGSHOT_FAR_MUL = 1.7; // long-range reward ceiling
export function longShotRangeMul(dist) {
    if (dist <= 6)
        return LONGSHOT_NEAR_MUL;
    if (dist >= LONGSHOT_FAR_DIST)
        return LONGSHOT_FAR_MUL;
    if (dist < LONGSHOT_NEUTRAL_DIST) {
        const t = (dist - 6) / (LONGSHOT_NEUTRAL_DIST - 6);
        return LONGSHOT_NEAR_MUL + t * (1 - LONGSHOT_NEAR_MUL);
    }
    const t = (dist - LONGSHOT_NEUTRAL_DIST) / (LONGSHOT_FAR_DIST - LONGSHOT_NEUTRAL_DIST);
    return 1 + t * (LONGSHOT_FAR_MUL - 1);
}
/** Linear damage falloff: full ≤ start, scaled to endFraction at end. The
 *  optional `modifier` is the room's ROUND MODIFIER id; under "longshot" the
 *  weapon's own falloff is REPLACED by the inverted range curve above. Any other
 *  value (including the default undefined) runs the byte-identical standard
 *  falloff, so every non-LONG-SHOT match is unchanged. Damage is
 *  server-authoritative — the client never calls this — so there is no
 *  prediction/reconcile surface. */
export function falloffDamage(cfg, dist, modifier) {
    if (modifier === "longshot")
        return cfg.damage * longShotRangeMul(dist);
    const [start, end, endFrac] = cfg.falloff;
    if (dist <= start)
        return cfg.damage;
    if (dist >= end)
        return cfg.damage * endFrac;
    const t = (dist - start) / (end - start);
    return cfg.damage * (1 - t * (1 - endFrac));
}
//# sourceMappingURL=weapons.js.map